import axios from 'axios'
import { hostUI } from '../../configuraciones'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

const initialState = {
    records: [],
    pagination: {
        totalRecords: 0,
        totalPages: 1,
        currentPage: 1,
        limit: 10
    },
    loading: false,
    error: null
}

const OBTENER_MOLIENDA_LISTA = 'bitacoraMolienda/obtenerLista'
const GUARDAR_MOLIENDA_RECORD = 'bitacoraMolienda/guardarRecord'
const ACTUALIZAR_MOLIENDA_RECORD = 'bitacoraMolienda/actualizarRecord'
const CAMBIAR_ESTATUS_MOLIENDA = 'bitacoraMolienda/cambiarEstatus'
const ELIMINAR_MOLIENDA_RECORD = 'bitacoraMolienda/eliminarRecord'

// GET list of molienda records from BackendInspeccion (hostUI)
export const obtenerMoliendaRecords = createAsyncThunk(
    OBTENER_MOLIENDA_LISTA,
    async (params, { rejectWithValue }) => {
        try {
            let idLogin = null;
            let page = 1;
            let limit = 10;
            let search = '';

            if (typeof params === 'object' && params !== null) {
                idLogin = params.idLogin || params.id_login || null;
                page = params.page || 1;
                limit = params.limit || 10;
                search = params.search || '';
            } else {
                idLogin = params;
            }

            const queryParts = [];
            if (idLogin) queryParts.push(`idLogin=${idLogin}`);
            if (page) queryParts.push(`page=${page}`);
            if (limit) queryParts.push(`limit=${limit}`);
            if (search) queryParts.push(`search=${encodeURIComponent(search)}`);

            const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';
            const url = `${hostUI}bitacora-produccion/molienda${queryString}`;

            const res = await axios.get(url);
            return {
                records: res.data.data || [],
                pagination: res.data.pagination || { totalRecords: (res.data.data || []).length, totalPages: 1, currentPage: 1, limit: 10 }
            };
        } catch (error) {
            console.error("Error al obtener registros de molienda:", error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
)

// POST create new record
export const guardarMoliendaRecord = createAsyncThunk(
    GUARDAR_MOLIENDA_RECORD,
    async (recordData, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/molienda`
            const res = await axios.post(url, recordData)
            return res.data.data
        } catch (error) {
            console.error("Error al crear registro de molienda:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT update existing record
export const actualizarMoliendaRecord = createAsyncThunk(
    ACTUALIZAR_MOLIENDA_RECORD,
    async ({ id, recordData }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/molienda/${id}`
            const res = await axios.put(url, recordData)
            return res.data.data
        } catch (error) {
            console.error("Error al actualizar registro de molienda:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// PUT update status & observations
export const actualizarEstatusMoliendaRecord = createAsyncThunk(
    CAMBIAR_ESTATUS_MOLIENDA,
    async ({ id, estatus, observaciones, observacionesCorreccion, observaciones_correccion }, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/molienda/${id}/estatus`
            const obsCorr = observacionesCorreccion || observaciones_correccion || observaciones;
            const res = await axios.put(url, { estatus, observaciones: obsCorr, observacionesCorreccion: obsCorr, observaciones_correccion: obsCorr })
            return res.data.data
        } catch (error) {
            console.error("Error al actualizar estatus de molienda:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// DELETE record
export const eliminarMoliendaRecord = createAsyncThunk(
    ELIMINAR_MOLIENDA_RECORD,
    async (id, { rejectWithValue }) => {
        try {
            const url = `${hostUI}bitacora-produccion/molienda/${id}`
            await axios.delete(url)
            return id
        } catch (error) {
            console.error("Error al eliminar registro de molienda:", error)
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

const bitacoraMoliendaSlice = createSlice({
    name: 'bitacoraMolienda',
    initialState,
    reducers: {
        resetMoliendaError: (state) => {
            state.error = null
        }
    },
    extraReducers: (builder) => {
        builder
            // Obtener Lista
            .addCase(obtenerMoliendaRecords.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(obtenerMoliendaRecords.fulfilled, (state, { payload }) => {
                state.loading = false
                state.records = payload.records
                state.pagination = payload.pagination
            })
            .addCase(obtenerMoliendaRecords.rejected, (state, { payload }) => {
                state.loading = false
                state.error = payload
            })

            // Guardar Nuevo
            .addCase(guardarMoliendaRecord.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(guardarMoliendaRecord.fulfilled, (state, { payload }) => {
                state.loading = false
                state.records.unshift(payload)
            })
            .addCase(guardarMoliendaRecord.rejected, (state, { payload }) => {
                state.loading = false
                state.error = payload
            })

            // Actualizar Existente
            .addCase(actualizarMoliendaRecord.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(actualizarMoliendaRecord.fulfilled, (state, { payload }) => {
                state.loading = false
                const idx = state.records.findIndex(r => r.id === payload.id)
                if (idx !== -1) state.records[idx] = { ...state.records[idx], ...payload }
            })
            .addCase(actualizarMoliendaRecord.rejected, (state, { payload }) => {
                state.loading = false
                state.error = payload
            })

            // Cambiar Estatus
            .addCase(actualizarEstatusMoliendaRecord.fulfilled, (state, { payload }) => {
                const idx = state.records.findIndex(r => r.id === payload.id)
                if (idx !== -1) {
                    state.records[idx].status = payload.status
                    if (payload.observaciones) state.records[idx].observaciones = payload.observaciones
                }
            })

            // Eliminar
            .addCase(eliminarMoliendaRecord.fulfilled, (state, { payload }) => {
                state.records = state.records.filter(r => r.id !== payload)
            })
    }
})

export const { resetMoliendaError } = bitacoraMoliendaSlice.actions
export default bitacoraMoliendaSlice.reducer
